// Card 27, banks (R548, ui-map-interface.md §17.3.27).
//
// A chart names a shoal of ground as a bank, "Dogger Bank", inside a dotted
// outline. Here a group of deposit fields that stand within `join_cells` of
// one another is one bank, with a name from the bank pool, so an order in
// chat says "meet at Kelp Bank" and both readers mean the same four fields.
//
// **THE GROUPS ARE A UNION-FIND OVER HEX DISTANCE**, so a chain of fields
// that each lie two cells from the next is ONE bank however long it runs.
// The outline is the union of the fields' own cells and their ring 1
// (`hex-ring.ts`), which is the water a gathering fleet works in.
//
// **A WORKED-OUT BANK DRAWS NOTHING.** A bank whose every field reads
// `amount == 0` is not a destination, so it carries no outline and no name.
// The site frames move `amount`, and `'sites'` packs the piece again.
//
// **THE NAME IS DRAWN BY THE BANK'S LOWEST SITE ID.** The banks are ordered
// on that, and `ctx.names.banks(k)` takes the k-th name of the pool's own
// draw order, so two commanders reading one world read one name.

import { CHART } from '../../../gen/presentation';
import { RESOURCES } from '../../../gen/balance';
import { chart as S } from '../../../gen/strings';
import { SiteKind, type Site } from '../../../gen/proto/aquaphobia';
import { fish } from '../../../render/chart-glyphs';
import { cellsWithin, hexDistance, unionOutline } from '../../../render/hex-ring';
import { offsetLoop } from '../../../render/offset-loop';
import { cellCenter } from '../../../world';
import type { Loop } from '../../../render/contours';
import type { Pt } from '../../../render/text-run';
import type { ChartContext, ChartSinks, Piece } from '../context';
import {
  CHART_INK, asPts, crownOf, dashedLoop, faceOf, lengthTo, longestLoop,
} from './shared';

/** The figures the piece reads out of its own table. */
const T = CHART.pieces.banks;

/** One bank: its outline, its name and the tally under the name. */
interface Bank {
  loops: Loop[];
  /** The longest loop, pushed out for the historical name to ride on. */
  arc: Pt[];
  arcStartS: number;
  name: string;
  /** `4 fields · 3 Al · 1 Vo`. */
  sub: string;
  cx: number;
  cy: number;
}

export class BanksPiece implements Piece {
  readonly id = 'banks' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The ground the fight happened over. It stays under a replay film. */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'sites', 'reading', 'zoomband'] as const;

  private banks: Bank[] = [];

  visible(ctx: ChartContext): boolean { return ctx.names !== null; }

  rebuild(ctx: ChartContext): void {
    this.banks = [];
    const named = ctx.names;
    if (!named) return;
    const fields = [...ctx.state.sites.values()]
      .filter((s) => s.kind === SiteKind.SITE_KIND_DEPOSIT)
      .sort((a, b) => a.id - b.id);
    for (const [k, group] of groupsOf(fields).entries()) {
      if (group.length < T.fig.min_fields) continue;
      if (group.every((s) => s.amount === 0)) continue;
      const cells = new Set<number>();
      let sx = 0, sy = 0;
      for (const s of group) {
        for (const c of cellsWithin(s.cell, 1)) cells.add(c);
        const [cx, cy] = cellCenter(s.cell);
        sx += cx; sy += cy;
      }
      const loops = unionOutline(cells).filter((l) => l.length >= 3);
      const widest = longestLoop(loops);
      if (!widest) continue;
      const rim = offsetLoop(widest, T.fig.name_offset);
      this.banks.push({
        loops,
        arc: asPts(rim),
        arcStartS: lengthTo(rim, crownOf(rim)),
        name: named.banks(k),
        sub: tallyOf(group),
        cx: sx / group.length,
        cy: sy / group.length,
      });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const style = ctx.cfg.pieces.banks[ctx.reading];
    const modern = ctx.reading === 'modern';
    const face = faceOf(ctx.reading);
    for (const b of this.banks) {
      for (const loop of b.loops) {
        dashedLoop(sinks.segments, loop, T.fig.width, CHART_INK.pale, T.fig.alpha, style.dash);
      }
      if (!modern) {
        ctx.glyphs.wordAlong(
          sinks.quads, b.name, b.arc, face, T.text.main,
          CHART_INK.pale, style.alpha,
          { anchor: 'centre', startS: b.arcStartS, closed: true },
        );
        continue;
      }
      // The name and the tally are one block, so they grow about one pivot.
      const pivot = { x: b.cx, y: b.cy };
      ctx.glyphs.word(
        sinks.quads, b.name, b.cx, b.cy + T.fig.label_dy,
        face, T.text.main, CHART_INK.pale, style.alpha, { anchor: 'centre', pivot },
      );
      ctx.glyphs.word(
        sinks.quads, b.sub, b.cx, b.cy + T.fig.sub_dy,
        face, T.text.sub, CHART_INK.faint, T.modern.fig.sub_alpha,
        { anchor: 'centre', pivot },
      );
      fish(
        sinks.segments, b.cx + T.fig.fish_dx, b.cy + T.fig.fish_dy,
        CHART_INK.pale, style.alpha,
      );
    }
  }

  /** The banks the last rebuild found. The test reads them. */
  get found(): readonly { name: string; sub: string }[] { return this.banks; }
}

/**
 * The deposit fields, grouped by hex distance, in ascending lowest-site-id
 * order.
 *
 * It is a union-find without the tree: the list is short — a world holds a
 * few dozen fields — and the pairwise walk is one rebuild per hello.
 */
function groupsOf(fields: readonly Site[]): Site[][] {
  const parent = fields.map((_, i) => i);
  const find = (i: number): number => {
    let r = i;
    while (parent[r] !== r) r = parent[r];
    while (parent[i] !== r) { const n = parent[i]; parent[i] = r; i = n; }
    return r;
  };
  for (let i = 0; i < fields.length; i++) {
    for (let j = i + 1; j < fields.length; j++) {
      if (hexDistance(fields[i].cell, fields[j].cell) > T.fig.join_cells) continue;
      const a = find(i), b = find(j);
      if (a !== b) parent[a] = b;
    }
  }
  const by = new Map<number, Site[]>();
  for (let i = 0; i < fields.length; i++) {
    const root = find(i);
    const held = by.get(root);
    if (held) held.push(fields[i]);
    else by.set(root, [fields[i]]);
  }
  // The fields are already in ascending id order, so a group's first entry
  // is its lowest id and the map's insertion order is that order.
  return [...by.values()];
}

/** `4 fields · 3 Al · 1 Vo`, from the pack's own commodity shorts. */
function tallyOf(group: readonly Site[]): string {
  const counts = new Map<number, number>();
  for (const s of group) counts.set(s.resource, (counts.get(s.resource) ?? 0) + 1);
  const parts: string[] = [];
  for (const r of RESOURCES) {
    const n = counts.get(r.id);
    if (n) parts.push(S.bank_part({ count: n, short: r.short }));
  }
  return S.bank_sub_modern({ fields: group.length, yield: parts.join(' · ') });
}

/** The one instance the registry holds. */
export const banks = new BanksPiece();
